"use client"
import React, { forwardRef, useEffect, useRef, useState } from "react"
import { classNames } from "../../utils"
import { InlineFlex, InlineFlexProps } from "../InlineFlex"
import { Input, InputProps } from "../Input"

export type AuthCodeProps = Omit<
  InlineFlexProps,
  "asChild" | "children" | "onChange"
> & {
  length?: number
  overrides?: {
    Container?: Partial<Pick<InlineFlexProps, "className" | "style">>
    /**
     * Overrides that apply to each of the individual the Input components.
     */
    Inputs?: Partial<InputProps>
  }
  onComplete: (authCode: string) => unknown
  value?: string
  onChange?: (authCode: string) => unknown
} & Pick<InputProps, "autoFocus" | "disabled" | "error">

type HandledKeys = "Backspace" | "ArrowLeft" | "ArrowRight"

const DEFAULT_LENGTH = 6
export const EMPTY_VALUE = ""

const isNumber = (value: string): boolean => {
  return !!value.match(/\d+/)
}

const isValidAuthCode = (code: string, expectedLength: number): boolean => {
  return (
    code.length === expectedLength &&
    code.split("").every((element: string) => isNumber(element))
  )
}

const valueToCode = (value: string, length: number): string[] => {
  const code = value.split("")
  if (code.length > length) {
    return code.slice(0, length)
  } else {
    while (code.length < length) {
      code.push(EMPTY_VALUE)
    }
  }
  return code
}

const codeToValue = (code: string[]): string => {
  const firstFilledIndex = code.findIndex(value => value !== EMPTY_VALUE)
  if (firstFilledIndex === -1) {
    return ""
  }
  return Array(firstFilledIndex).fill(" ").join("") + code.join("")
}

export const AuthCode = forwardRef<HTMLDivElement, AuthCodeProps>(
  function AuthCode(
    {
      autoFocus,
      className,
      disabled,
      error,
      length = DEFAULT_LENGTH,
      overrides,
      style,
      onComplete,
      value,
      onChange,
    },
    ref,
  ) {
    const [code, setCode] = useState<string[]>(
      value ? valueToCode(value, length) : Array(length).fill(EMPTY_VALUE),
    )
    const handleCodeChange = (
      newCode: string[],
      {
        valuePropChanged = false,
        shouldBlur = false,
      }: { valuePropChanged?: boolean; shouldBlur?: boolean } = {},
    ) => {
      setCode(newCode)

      const newValue = codeToValue(newCode)
      if (!valuePropChanged) {
        onChange?.(newValue)
      }

      if (isValidAuthCode(newValue, length)) {
        if (shouldBlur) {
          inputsRef.current.forEach(input => input.blur())
        }
        onComplete(newValue)
      }
    }
    useEffect(() => {
      handleCodeChange(valueToCode(value ?? "", length), {
        valuePropChanged: true,
      })
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, length])

    const inputsRef = useRef<HTMLInputElement[]>([])

    const updateCodeValue = (index: number, value: string) => {
      const newCode = [...code]
      newCode[index] = value
      for (let i = index + 1; i < newCode.length; i++) {
        newCode[i] = EMPTY_VALUE
      }

      handleCodeChange(newCode)
    }

    const focusNext = (currentIndex: number) => {
      const nextInput = inputsRef.current[currentIndex + 1]
      nextInput?.focus()

      if (!nextInput) {
        const lastInput = inputsRef.current[currentIndex]
        lastInput?.blur()
      }
    }

    const pasteCode = (code: string) => {
      const sanitizedCode = code.trim().split("").filter(isNumber)
      const truncatedCode = Array(length).fill(EMPTY_VALUE)
      for (let i = 0; i < truncatedCode.length; i++) {
        truncatedCode[i] = sanitizedCode[i] || EMPTY_VALUE
      }
      handleCodeChange(truncatedCode, { shouldBlur: true })
    }

    const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
      const pastedCode = event.clipboardData.getData("Text")
      pasteCode(pastedCode)
      event.preventDefault()
    }

    const handleChange = (
      event: React.ChangeEvent<HTMLInputElement>,
      index: number,
    ) => {
      const { value } = event.target
      if (value.length === 1 && isNumber(value)) {
        updateCodeValue(index, value)
        focusNext(index)
      }
    }

    const handleInput = (event: React.FormEvent<HTMLInputElement>) => {
      // This is a workaround for automatically filling codes from the iOS SMS autofill,
      // since it's triggered via onInput rather than an onChange or onPaste event.
      const { value } = event.target as HTMLInputElement
      if (value && value.length > 1 && isNumber(value)) {
        pasteCode(value)
      }
    }

    const handleKeyDown = (
      event: React.KeyboardEvent<HTMLInputElement>,
      index: number,
    ) => {
      const { key } = event
      const hasValue = !!code[index]

      const focusPrevious = (currentIndex: number) => {
        const previousInput = inputsRef.current[currentIndex - 1]
        if (previousInput) {
          event.preventDefault()
          previousInput.focus()
        }
      }

      switch (key as HandledKeys) {
        case "Backspace": {
          if (hasValue) {
            updateCodeValue(index, EMPTY_VALUE)
          }
          if (index > 0) {
            updateCodeValue(index - 1, EMPTY_VALUE)
          }
          focusPrevious(index)
          break
        }
        case "ArrowLeft": {
          focusPrevious(index)
          break
        }
        case "ArrowRight": {
          event.preventDefault()
          focusNext(index)
          break
        }
        default: {
          const isReplacingValue = isNumber(key) && hasValue
          if (isReplacingValue) {
            event.preventDefault()
            updateCodeValue(index, key)
            focusNext(index)
          }
        }
      }
    }

    return (
      <InlineFlex
        aria-disabled={disabled}
        className={classNames("gap-2", className)}
        ref={ref}
        style={style}
        {...overrides?.Container}
      >
        {code.map((value, index) => (
          <Input
            aria-label={`Digit ${index + 1}`}
            autoComplete={index === 0 ? "one-time-code" : "off"}
            autoFocus={index === 0 && autoFocus}
            className={classNames(
              // !size-10 to override default w-full on input.
              "flex !size-10 flex-none items-center justify-center rounded-lg p-0",
              overrides?.Inputs?.className,
            )}
            data-testid={`input-${index}`}
            disabled={disabled}
            error={error}
            inputMode="numeric"
            inputRef={(element: HTMLInputElement) => {
              inputsRef.current[index] = element
            }}
            key={index}
            overrides={{
              Input: {
                style: {
                  textAlign: "center",
                },
              },
            }}
            type="text"
            value={value}
            onChange={event => handleChange(event, index)}
            onInput={handleInput}
            onKeyDown={event => handleKeyDown(event, index)}
            onPaste={handlePaste}
          />
        ))}
      </InlineFlex>
    )
  },
)
